/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * The flatscreen / mobile game HUD for the grow-a-garden loop:
 *   - a coin balance (top-left),
 *   - mode buttons (Plant / Harvest / Explore) — the ONLY way to switch modes
 *     outside VR, where there is no in-world selection wheel,
 *   - a seed shop (bottom) with prices, rarity colours and the region's
 *     signature seed highlighted,
 *   - transient toasts driven by the `garden-notify` event.
 *
 * The overlay owns only DOM + input; GardenHudSystem binds it to the ECS world
 * (wallet, seedbox selection, interaction mode) each frame.
 */
// Shown when the player hasn't chosen a garden name yet.
const DEFAULT_BRAND_NAME = 'Garden XR Social';
// Default HUD accent (matches the green baked into overlay.css).
const DEFAULT_ACCENT = '#5ec26a';
// Cozy accent palette the player can theme their HUD with.
const ACCENT_SWATCHES = [
	'#5ec26a', // meadow green
	'#4aa3ff', // sky blue
	'#ffb01f', // marigold
	'#f2789f', // rose
	'#b45cff', // lavender
	'#ff7a59', // coral
];

export class GardenHudOverlay {
	constructor() {
		this.onSetMode = null; // (modeName: 'plant'|'harvest'|'explore') => void
		this.onSelectSeed = null; // (groupIndex: number) => void
		this.onToggleShop = null; // () => void
		this.onHarvest = null; // () => void, from the welcome-back card
		this.onSavePersonalization = null; // (name: string, accent: string) => void
		this.onEnableNotifications = null; // (enable: boolean) => void
		this.onOpenGifts = null; // () => void, refresh the neighbours list
		this.onSendGift = null; // (toUid: string, toName: string) => void
		this._welcomeTimer = null;
		this._accentColor = '';
		this._gardenName = '';

		this._seeds = [];
		this._shopOpen = false;
		this._giftsOpen = false;

		this._buildDom();
		this._bindKeyboard();
		this._bindNotifications();
	}

	_buildDom() {
		this.root = document.createElement('div');
		this.root.classList.add('garden-hud');
		this.root.style.display = 'none';

		// Coin balance.
		this.coinBar = document.createElement('div');
		this.coinBar.classList.add('garden-coins');
		this.coinBar.innerHTML =
			`<img class="garden-coin-icon" src="assets/images/ui/hud/coin.png" alt="coins" ` +
			`draggable="false" onerror="this.replaceWith(document.createTextNode('🪙'))"/>` +
			`<span class="garden-coin-value">0</span>`;
		this.coinValue = this.coinBar.querySelector('.garden-coin-value');
		this.root.appendChild(this.coinBar);

		// Live leaderboard / "who's in the world" (top-right).
		this.leaderboard = document.createElement('div');
		this.leaderboard.classList.add('garden-leaderboard');
		this.leaderboard.innerHTML =
			`<div class="garden-lb-header">🌍 Live Garden</div>` +
			`<div class="garden-lb-you"></div>` +
			`<div class="garden-lb-total"><span class="garden-lb-count">–</span> visitors</div>` +
			`<ul class="garden-lb-countries"></ul>`;
		this.lbYou = this.leaderboard.querySelector('.garden-lb-you');
		this.lbCount = this.leaderboard.querySelector('.garden-lb-count');
		this.lbCountries = this.leaderboard.querySelector('.garden-lb-countries');
		this.root.appendChild(this.leaderboard);

		// Mode buttons.
		this.modeBar = document.createElement('div');
		this.modeBar.classList.add('garden-mode-bar');
		this.modeButtons = {};

		// One-tap contextual hint — shows what a tap will do right now (plant the
		// selected seed / harvest). The mode is chosen automatically by
		// ContextualActionSystem, so flatscreen/mobile no longer needs manual
		// Plant / Harvest / Explore buttons.
		this.contextChip = document.createElement('div');
		this.contextChip.classList.add('garden-context-chip');
		this.modeBar.appendChild(this.contextChip);

		// Water stays a deliberate, sticky mode — keep it as a toggle button.
		const waterBtn = document.createElement('button');
		waterBtn.classList.add('garden-mode-btn');
		waterBtn.innerHTML = this._btnContent(null, 'Water', 'water');
		waterBtn.addEventListener('click', () => {
			if (this.onSetMode) this.onSetMode('water');
		});
		this.modeBar.appendChild(waterBtn);
		this.modeButtons['water'] = waterBtn;

		// Shop toggle.
		this.shopToggle = document.createElement('button');
		this.shopToggle.classList.add('garden-mode-btn', 'garden-shop-toggle');
		this.shopToggle.innerHTML = this._btnContent(null, 'Shop', 'shop');
		this.shopToggle.addEventListener('click', () => this.toggleShop());
		this.modeBar.appendChild(this.shopToggle);

		// Neighbours / gifting toggle (uses the gift HUD icon).
		this.giftsToggle = document.createElement('button');
		this.giftsToggle.classList.add('garden-mode-btn', 'garden-gifts-toggle');
		this.giftsToggle.innerHTML = this._btnContent(null, 'Neighbours', 'gift');
		this.giftsToggle.addEventListener('click', () => this.toggleGifts());
		this.modeBar.appendChild(this.giftsToggle);

		this.root.appendChild(this.modeBar);

		// Shop panel.
		this.shopPanel = document.createElement('div');
		this.shopPanel.classList.add('garden-shop');
		this.shopPanel.style.display = 'none';
		this.shopHeader = document.createElement('div');
		this.shopHeader.classList.add('garden-shop-header');
		this.shopHeader.textContent = 'Seed Shop';
		this.shopPanel.appendChild(this.shopHeader);
		this.shopGrid = document.createElement('div');
		this.shopGrid.classList.add('garden-shop-grid');
		this.shopPanel.appendChild(this.shopGrid);
		this.root.appendChild(this.shopPanel);

		// Neighbours panel: who else gardens in the world + a cozy "send a gift".
		this.giftsPanel = document.createElement('div');
		this.giftsPanel.classList.add('garden-gifts');
		this.giftsPanel.style.display = 'none';
		this.giftsHeader = document.createElement('div');
		this.giftsHeader.classList.add('garden-gifts-header');
		this.giftsHeader.textContent = '🎁 Neighbours';
		this.giftsPanel.appendChild(this.giftsHeader);
		this.giftsList = document.createElement('div');
		this.giftsList.classList.add('garden-gifts-list');
		this.giftsPanel.appendChild(this.giftsList);
		this.root.appendChild(this.giftsPanel);

		// Toasts.
		this.toastContainer = document.createElement('div');
		this.toastContainer.classList.add('garden-toasts');
		this.root.appendChild(this.toastContainer);

		// "While you were away" welcome-back card (shown once per session on load).
		this.welcomeCard = document.createElement('div');
		this.welcomeCard.classList.add('garden-welcome');
		this.welcomeCard.style.display = 'none';
		this.welcomeCard.innerHTML =
			`<button class="garden-welcome-close" aria-label="Close">×</button>` +
			`<div class="garden-welcome-title"></div>` +
			`<div class="garden-welcome-sub"></div>` +
			`<div class="garden-welcome-actions">` +
			`<button class="garden-welcome-harvest">✂️ Harvest now</button>` +
			`<button class="garden-welcome-dismiss">Later</button>` +
			`</div>`;
		this.welcomeTitle = this.welcomeCard.querySelector('.garden-welcome-title');
		this.welcomeSub = this.welcomeCard.querySelector('.garden-welcome-sub');
		this.welcomeCard
			.querySelector('.garden-welcome-close')
			.addEventListener('click', () => this.hideWelcomeBack());
		this.welcomeCard
			.querySelector('.garden-welcome-dismiss')
			.addEventListener('click', () => this.hideWelcomeBack());
		this.welcomeCard
			.querySelector('.garden-welcome-harvest')
			.addEventListener('click', () => {
				if (this.onHarvest) this.onHarvest();
				this.hideWelcomeBack();
			});
		this.root.appendChild(this.welcomeCard);

		// Brand mark + early-access BETA badge + personalize button (top-centre).
		// The name label doubles as the player's garden name once they set one.
		this.brand = document.createElement('div');
		this.brand.classList.add('garden-brand');
		this.brand.innerHTML =
			`<img class="garden-brand-logo" src="assets/images/ui/brand/logo.png" ` +
			`alt="" draggable="false" onerror="this.style.display='none'"/>` +
			`<span class="garden-brand-name">${DEFAULT_BRAND_NAME}</span>` +
			`<span class="garden-brand-beta">BETA</span>` +
			`<button class="garden-brand-edit" aria-label="Personalize your garden" ` +
			`title="Personalize your garden">✏️</button>`;
		this.brandName = this.brand.querySelector('.garden-brand-name');
		this.brand
			.querySelector('.garden-brand-edit')
			.addEventListener('click', () => this.openPersonalize());
		this.root.appendChild(this.brand);

		this._buildPersonalizePanel();

		// Harvest-notification opt-in (shown once by GardenNotificationSystem).
		this.notifyOptIn = document.createElement('div');
		this.notifyOptIn.classList.add('garden-notify-optin');
		this.notifyOptIn.style.display = 'none';
		this.notifyOptIn.innerHTML =
			`<div class="garden-notify-text">🔔 Get a nudge when your garden's ready to harvest?</div>` +
			`<div class="garden-notify-actions">` +
			`<button class="garden-notify-enable">Enable</button>` +
			`<button class="garden-notify-later">Not now</button>` +
			`</div>`;
		this.notifyOptIn
			.querySelector('.garden-notify-enable')
			.addEventListener('click', () => {
				if (this.onEnableNotifications) this.onEnableNotifications(true);
			});
		this.notifyOptIn
			.querySelector('.garden-notify-later')
			.addEventListener('click', () => {
				if (this.onEnableNotifications) this.onEnableNotifications(false);
			});
		this.root.appendChild(this.notifyOptIn);

		document.body.appendChild(this.root);
	}

	showNotifyOptIn() {
		this.notifyOptIn.style.display = 'block';
		requestAnimationFrame(() => this.notifyOptIn.classList.add('visible'));
	}

	hideNotifyOptIn() {
		this.notifyOptIn.classList.remove('visible');
		setTimeout(() => {
			this.notifyOptIn.style.display = 'none';
		}, 300);
	}

	/**
	 * Build the "Personalize your garden" panel: a name field + accent swatches.
	 * Hidden until the player taps the ✏️ on the brand chip.
	 */
	_buildPersonalizePanel() {
		this.personalizePanel = document.createElement('div');
		this.personalizePanel.classList.add('garden-personalize');
		this.personalizePanel.style.display = 'none';

		const swatches = ACCENT_SWATCHES.map(
			(c) =>
				`<button class="garden-swatch" data-color="${c}" ` +
				`style="background:${c}" aria-label="Accent ${c}"></button>`,
		).join('');

		this.personalizePanel.innerHTML =
			`<button class="garden-personalize-close" aria-label="Close">×</button>` +
			`<div class="garden-personalize-title">Personalize your garden</div>` +
			`<label class="garden-personalize-label" for="garden-name-input">Garden name</label>` +
			`<input class="garden-personalize-input" id="garden-name-input" type="text" ` +
			`maxlength="24" placeholder="${DEFAULT_BRAND_NAME}" autocomplete="off"/>` +
			`<div class="garden-personalize-label">Accent colour</div>` +
			`<div class="garden-swatches">${swatches}</div>` +
			`<div class="garden-personalize-actions">` +
			`<button class="garden-personalize-save">Save</button>` +
			`</div>`;

		this.nameInput = this.personalizePanel.querySelector(
			'.garden-personalize-input',
		);
		this.swatchButtons = Array.from(
			this.personalizePanel.querySelectorAll('.garden-swatch'),
		);
		this._pendingAccent = DEFAULT_ACCENT;

		this.swatchButtons.forEach((btn) => {
			btn.addEventListener('click', () => {
				this._pendingAccent = btn.dataset.color;
				this._markSelectedSwatch(this._pendingAccent);
			});
		});

		this.personalizePanel
			.querySelector('.garden-personalize-close')
			.addEventListener('click', () => this.closePersonalize());
		this.personalizePanel
			.querySelector('.garden-personalize-save')
			.addEventListener('click', () => this._savePersonalize());
		// Enter in the name field saves.
		this.nameInput.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') this._savePersonalize();
			// Don't let gameplay keybinds (P/H/G/Tab/digits) fire while typing.
			e.stopPropagation();
		});

		this.root.appendChild(this.personalizePanel);
	}

	_markSelectedSwatch(color) {
		this.swatchButtons.forEach((btn) => {
			btn.classList.toggle('selected', btn.dataset.color === color);
		});
	}

	openPersonalize() {
		this.nameInput.value = this._gardenName || '';
		this._pendingAccent = this._accentColor || DEFAULT_ACCENT;
		this._markSelectedSwatch(this._pendingAccent);
		this.personalizePanel.style.display = 'block';
		// Release pointer lock so desktop players can type / click swatches.
		if (document.exitPointerLock) document.exitPointerLock();
		requestAnimationFrame(() => this.nameInput.focus());
	}

	closePersonalize() {
		this.personalizePanel.style.display = 'none';
	}

	_savePersonalize() {
		const name = (this.nameInput.value || '').trim();
		const accent = this._pendingAccent || DEFAULT_ACCENT;
		if (this.onSavePersonalization) this.onSavePersonalization(name, accent);
		this.closePersonalize();
	}

	/**
	 * Apply a saved/loaded identity to the HUD: name label + accent theme.
	 * @param {{ gardenName?: string, accentColor?: string }} identity
	 */
	setIdentity(identity) {
		if (!identity) return;
		this._gardenName = identity.gardenName || '';
		this._accentColor = identity.accentColor || '';
		if (this.brandName) {
			this.brandName.textContent = this._gardenName || DEFAULT_BRAND_NAME;
		}
		this.root.style.setProperty(
			'--gx-accent',
			this._accentColor || DEFAULT_ACCENT,
		);
	}

	/**
	 * Inner HTML for a mode/shop button: a HUD icon image when we have art for
	 * it (assets/images/ui/hud/<icon>.png, hidden on error), else an emoji, plus
	 * the text label.
	 */
	_btnContent(emoji, label, icon) {
		let art = '';
		if (icon) {
			art =
				`<img class="garden-btn-icon" src="assets/images/ui/hud/${icon}.png" ` +
				`alt="" draggable="false" onerror="this.style.display='none'"/>`;
		} else if (emoji) {
			art = `<span class="garden-btn-emoji">${emoji}</span>`;
		}
		return `${art}<span class="garden-btn-label">${label}</span>`;
	}

	_bindKeyboard() {
		this._onKeyDown = (e) => {
			// Ignore auto-repeat and typing into inputs.
			if (e.repeat) return;
			switch (e.code) {
				case 'KeyP':
					if (this.onSetMode) this.onSetMode('plant');
					break;
				case 'KeyH':
					if (this.onSetMode) this.onSetMode('harvest');
					break;
				case 'KeyG':
					if (this.onSetMode) this.onSetMode('explore');
					break;
				case 'Tab':
					e.preventDefault();
					this.toggleShop();
					break;
				default:
					// Digit keys 1-9 quick-select a shop seed.
					if (/^Digit[1-9]$/.test(e.code)) {
						const idx = parseInt(e.code.slice(5), 10) - 1;
						const seed = this._seeds[idx];
						if (seed && this.onSelectSeed) this.onSelectSeed(seed.groupIndex);
					}
					break;
			}
		};
		window.addEventListener('keydown', this._onKeyDown);
	}

	_bindNotifications() {
		this._onNotify = (e) => {
			const { message, type } = e.detail || {};
			this.showToast(message, type);
		};
		window.addEventListener('garden-notify', this._onNotify);
	}

	showToast(message, type = 'info') {
		if (!message) return;
		const toast = document.createElement('div');
		toast.classList.add('garden-toast', `garden-toast-${type}`);
		toast.textContent = message;
		this.toastContainer.appendChild(toast);
		// Force reflow then fade in.
		requestAnimationFrame(() => toast.classList.add('visible'));
		setTimeout(() => {
			toast.classList.remove('visible');
			setTimeout(() => toast.remove(), 300);
		}, 1800);
	}

	setCoins(coins) {
		this.coinValue.textContent = String(Math.floor(coins));
	}

	/**
	 * Show the "while you were away" card. Auto-dismisses after a few seconds.
	 * @param {{ title: string, subtitle: string, showHarvest?: boolean }} data
	 */
	showWelcomeBack(data) {
		if (!data || !data.title) return;
		this.welcomeTitle.textContent = data.title;
		this.welcomeSub.textContent = data.subtitle || '';
		const harvestBtn = this.welcomeCard.querySelector(
			'.garden-welcome-harvest',
		);
		harvestBtn.style.display = data.showHarvest === false ? 'none' : '';

		this.welcomeCard.style.display = 'block';
		requestAnimationFrame(() => this.welcomeCard.classList.add('visible'));

		if (this._welcomeTimer) clearTimeout(this._welcomeTimer);
		this._welcomeTimer = setTimeout(() => this.hideWelcomeBack(), 9000);
	}

	hideWelcomeBack() {
		if (this._welcomeTimer) {
			clearTimeout(this._welcomeTimer);
			this._welcomeTimer = null;
		}
		this.welcomeCard.classList.remove('visible');
		setTimeout(() => {
			this.welcomeCard.style.display = 'none';
		}, 300);
	}

	/**
	 * Update the live leaderboard panel.
	 * @param {{ totalVisitors:number,
	 *   countries: Array<{name:string, count:number}>,
	 *   you?: {name:string, country:string} }} data
	 */
	setLeaderboard(data) {
		if (!data) return;
		this.lbCount.textContent = String(data.totalVisitors ?? 0);

		if (data.you && data.you.name) {
			const country =
				data.you.country && data.you.country !== 'Unknown'
					? ` · ${data.you.country}`
					: '';
			this.lbYou.textContent = `You: ${data.you.name}${country}`;
			this.lbYou.style.display = 'block';
		} else {
			this.lbYou.style.display = 'none';
		}

		const countries = data.countries || [];
		this.lbCountries.innerHTML = countries
			.slice(0, 5)
			.map(
				(c) =>
					`<li><span class="garden-lb-country">🌱 ${c.name}</span>` +
					`<span class="garden-lb-num">${c.count}</span></li>`,
			)
			.join('');
	}

	setActiveMode(modeName) {
		Object.entries(this.modeButtons).forEach(([mode, btn]) => {
			btn.classList.toggle('active', mode === modeName);
		});
	}

	/**
	 * Reflect the current one-tap context on the hint chip.
	 * @param {'plant'|'harvest'|'water'|'explore'} modeName
	 * @param {string} [seedName] selected seed's display name (for planting)
	 */
	setContextHint(modeName, seedName) {
		if (!this.contextChip) return;
		let text;
		if (modeName === 'harvest') {
			text = '✂️ Tap to harvest';
		} else if (modeName === 'water') {
			text = '💧 Watering — tap Water again to stop';
		} else if (modeName === 'plant') {
			text = seedName ? `🌱 Tap to plant ${seedName}` : '🌱 Tap to plant';
		} else {
			text = '🌱 Aim at soil to plant · at a ripe plant to harvest';
		}
		this.contextChip.textContent = text;
	}

	/**
	 * @param {Array<{family:string,displayName:string,cost:number,color:string,
	 *   groupIndex:number,affordable:boolean,signature:boolean,selected:boolean}>} seeds
	 */
	setSeeds(seeds) {
		this._seeds = seeds;
		this.shopGrid.innerHTML = '';
		seeds.forEach((seed, i) => {
			const card = document.createElement('button');
			card.classList.add('garden-seed-card');
			card.classList.toggle('unaffordable', !seed.affordable);
			card.classList.toggle('selected', !!seed.selected);
			card.style.borderColor = seed.color;
			// Seed-packet art (assets/images/ui/seeds/<family>.png). If a family
			// has no art yet, onerror hides the <img> and the card still reads fine.
			const thumb = seed.family
				? `<img class="garden-seed-thumb" src="assets/images/ui/seeds/${seed.family}.png" ` +
				  `alt="" draggable="false" onerror="this.style.display='none'"/>`
				: '';
			// Rare tiers get a sparkle badge (assets/images/ui/hud/sparkle.png).
			const rare = seed.rarity === 'epic' || seed.rarity === 'legendary';
			const sparkle = rare
				? `<img class="garden-seed-sparkle" src="assets/images/ui/hud/sparkle.png" ` +
				  `alt="" draggable="false" onerror="this.style.display='none'"/>`
				: '';
			card.innerHTML =
				`<span class="garden-seed-key">${i < 9 ? i + 1 : ''}</span>` +
				sparkle +
				thumb +
				`<span class="garden-seed-name" style="color:${seed.color}">` +
				`${seed.signature ? '★ ' : ''}${seed.displayName}</span>` +
				`<span class="garden-seed-cost">🪙 ${seed.cost}</span>`;
			card.addEventListener('click', () => {
				if (this.onSelectSeed) this.onSelectSeed(seed.groupIndex);
			});
			this.shopGrid.appendChild(card);
		});
	}

	toggleShop() {
		if (this._shopOpen) {
			this.closeShop();
		} else {
			this.openShop();
		}
	}

	openShop() {
		this._shopOpen = true;
		this.shopPanel.style.display = 'block';
		this.shopToggle.classList.add('active');
		// Release pointer lock so the player can click shop cards on desktop.
		if (document.exitPointerLock) document.exitPointerLock();
		if (this.onToggleShop) this.onToggleShop();
	}

	closeShop() {
		this._shopOpen = false;
		this.shopPanel.style.display = 'none';
		this.shopToggle.classList.remove('active');
		if (this.onToggleShop) this.onToggleShop();
	}

	toggleGifts() {
		if (this._giftsOpen) {
			this.closeGifts();
		} else {
			this.openGifts();
		}
	}

	openGifts() {
		this._giftsOpen = true;
		if (this._shopOpen) this.closeShop();
		this.giftsPanel.style.display = 'block';
		this.giftsToggle.classList.add('active');
		// Show a loading hint until the neighbour list arrives.
		this.giftsList.innerHTML = `<div class="garden-gifts-empty">Loading neighbours…</div>`;
		if (document.exitPointerLock) document.exitPointerLock();
		if (this.onOpenGifts) this.onOpenGifts();
	}

	closeGifts() {
		this._giftsOpen = false;
		this.giftsPanel.style.display = 'none';
		this.giftsToggle.classList.remove('active');
	}

	/**
	 * Render the neighbours list (other players' plots) with a "send a gift"
	 * button per neighbour.
	 * @param {Array<{uid:string, name:string, plantCount:number}>} neighbours
	 */
	setNeighbours(neighbours) {
		const list = neighbours || [];
		if (!list.length) {
			this.giftsList.innerHTML =
				`<div class="garden-gifts-empty">No neighbours yet — invite a friend ` +
				`to plant alongside you! 🌱</div>`;
			return;
		}
		this.giftsList.innerHTML = '';
		list.forEach((n) => {
			const row = document.createElement('div');
			row.classList.add('garden-gift-row');
			const plants = n.plantCount || 0;
			const s = plants === 1 ? '' : 's';
			row.innerHTML =
				`<span class="garden-gift-name">${this._escape(n.name)}</span>` +
				`<span class="garden-gift-count">${plants} plant${s}</span>`;
			const btn = document.createElement('button');
			btn.classList.add('garden-gift-send');
			btn.innerHTML = `🎁 10`;
			btn.addEventListener('click', () => {
				if (this.onSendGift) this.onSendGift(n.uid, n.name);
				btn.disabled = true;
				btn.textContent = 'Sent ✓';
			});
			row.appendChild(btn);
			this.giftsList.appendChild(row);
		});
	}

	/** Minimal HTML-escape for player-supplied names rendered via innerHTML. */
	_escape(text) {
		const div = document.createElement('div');
		div.textContent = text == null ? '' : String(text);
		return div.innerHTML;
	}

	show() {
		this.root.style.display = 'block';
	}

	hide() {
		this.root.style.display = 'none';
	}
}
